import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment.development';

export interface Opcion {
  id_opcion?: number;
  texto_opcion: string;
  es_correcta?: boolean;
  orden: number;
  feedback?: string;
}

export interface Pregunta {
  id_pregunta: number;
  tipo_pregunta: 'opcion_multiple' | 'verdadero_falso' | 'seleccion_multiple' | 'respuesta_corta' | 'respuesta_larga';
  texto_pregunta: string;
  imagen_url?: string;
  explicacion?: string;
  puntos: string;
  orden: number;
  obligatoria: boolean;
  opciones?: Opcion[];
}

export interface Examen {
  id_examen: number;
  id_curso: number;
  id_modulo: number;
  titulo: string;
  descripcion: string;
  tiempo_limite: number;
  intentos_permitidos: number;
  nota_minima_aprobacion: string;
  mostrar_resultados_inmediatos: boolean;
  mostrar_respuestas_correctas: boolean;
  aleatorizar_preguntas: boolean;
  aleatorizar_opciones: boolean;
  estado: string;
  fecha_disponible: string;
  fecha_cierre: string;
  fecha_creacion: string;
  fecha_actualizacion: string;
  modulo?: {
    id_modulo: number;
    titulo_modulo: string;
  };
  preguntas: Pregunta[];
}

export interface Intento {
  id_intento: number;
  id_examen: number;
  id_usuario: number;
  fecha_inicio: string;
  fecha_fin?: string;
  tiempo_total?: number;
  puntuacion?: number;
  porcentaje?: number;
  estado: 'En_Progreso' | 'Completado' | 'Abandonado' | 'Tiempo_Agotado';
  aprobado?: boolean;
}

export interface RespuestaIntento {
  id_pregunta: number;
  id_opcion?: number;
  respuesta_texto?: string;
  tiempo_respuesta?: number;
}

export interface ExamenesResponse {
  ok: boolean;
  data?: Examen[];
  msg?: string;
}

export interface ExamenResponse {
  ok: boolean;
  data?: Examen;
  msg?: string;
}

export interface IntentoResponse {
  ok: boolean;
  data?: Intento;
  msg?: string;
}

export interface ResultadosResponse {
  ok: boolean;
  data?: any;
  msg?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExamenesService {
  private readonly API_URL = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Obtiene todos los exámenes de un curso
   */
  getExamenesCurso(idCurso: number): Observable<ExamenesResponse> {
    return this.http.get<ExamenesResponse>(`${this.API_URL}/examenes/mi-curso/${idCurso}`).pipe(
      tap(response => {
        console.log('📝 Exámenes del curso:', response);
      }),
      catchError(error => {
        console.error('Error al obtener exámenes:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al cargar los exámenes del curso'
        });
      })
    );
  }

  /**
   * Obtiene un examen específico por ID
   */
  getExamen(idExamen: number, incluirRespuestas: boolean = false): Observable<ExamenResponse> {
    const url = incluirRespuestas 
      ? `${this.API_URL}/examenes/${idExamen}?incluir_respuestas=true`
      : `${this.API_URL}/examenes/${idExamen}`;
      
    return this.http.get<ExamenResponse>(url).pipe(
      tap(response => {
        console.log('📝 Examen:', response);
      }),
      catchError(error => {
        console.error('Error al obtener examen:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al cargar el examen'
        });
      })
    );
  }

  /**
   * Inicia un intento de examen
   */
  iniciarIntento(idExamen: number): Observable<IntentoResponse> {
    return this.http.post<IntentoResponse>(`${this.API_URL}/intentos/${idExamen}/iniciar`, {}).pipe(
      tap(response => {
        console.log('▶️ Intento iniciado:', response);
      }),
      catchError(error => {
        console.error('Error al iniciar examen:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al iniciar el examen'
        });
      })
    );
  }

  /**
   * Responde una pregunta individual
   */
  responderPregunta(idIntento: number, respuesta: RespuestaIntento): Observable<any> {
    return this.http.post(`${this.API_URL}/intentos/${idIntento}/responder`, respuesta).pipe(
      tap(response => {
        console.log('✅ Respuesta guardada:', response);
      }),
      catchError(error => {
        console.error('Error al responder pregunta:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al guardar la respuesta'
        });
      })
    );
  }

  /**
   * Finaliza un intento de examen
   */
  finalizarIntento(idIntento: number): Observable<any> {
    return this.http.post(`${this.API_URL}/intentos/${idIntento}/finalizar`, {}).pipe(
      tap(response => {
        console.log('🏁 Intento finalizado:', response);
      }),
      catchError(error => {
        console.error('Error al finalizar examen:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al finalizar el examen'
        });
      })
    );
  }

  /**
   * Obtiene los resultados de un intento
   */
  getResultados(idIntento: number): Observable<ResultadosResponse> {
    return this.http.get<ResultadosResponse>(`${this.API_URL}/intentos/${idIntento}/resultados`).pipe(
      tap(response => {
        console.log('📊 Resultados del examen:', response);
      }),
      catchError(error => {
        console.error('Error al obtener resultados:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al cargar los resultados'
        });
      })
    );
  }

  /**
   * Obtiene todos los intentos del usuario en un examen
   */
  getMisIntentos(idExamen: number): Observable<any> {
    return this.http.get(`${this.API_URL}/intentos/mis-intentos/${idExamen}`).pipe(
      tap(response => {
        console.log('📋 Mis intentos:', response);
      }),
      catchError(error => {
        console.error('Error al obtener intentos:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al cargar los intentos'
        });
      })
    );
  }

  /**
   * Obtiene exámenes disponibles de un curso
   */
  getExamenesDisponibles(idCurso: number): Observable<ExamenesResponse> {
    return this.http.get<ExamenesResponse>(`${this.API_URL}/examenes/disponibles/${idCurso}`).pipe(
      tap(response => {
        console.log('📚 Exámenes disponibles:', response);
        
        // Detectar duplicados en la respuesta del backend
        if (response.ok && response.data) {
          response.data.forEach(examen => {
            if (examen.preguntas && examen.preguntas.length > 0) {
              const ids = examen.preguntas.map(p => p.id_pregunta);
              const idsUnicos = new Set(ids);
              
              if (ids.length !== idsUnicos.size) {
                const duplicados = ids.filter((id, index) => ids.indexOf(id) !== index);
                console.error(`🚨 [SERVICE] Examen ${examen.id_examen} (${examen.titulo}) tiene duplicados del BACKEND:`, {
                  total: ids.length,
                  unicos: idsUnicos.size,
                  duplicados: [...new Set(duplicados)],
                  curso: idCurso,
                  modulo: examen.id_modulo
                });
              } else {
                console.log(`✅ [SERVICE] Examen ${examen.id_examen} sin duplicados`);
              }
            }
          });
        }
      }),
      catchError(error => {
        console.error('Error al obtener exámenes disponibles:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al cargar los exámenes disponibles'
        });
      })
    );
  }
}

